import React, { Component } from 'react';
import moment from 'moment';
import { getTimesheetReport } from '../../../services/timeTrackingService';
import { convertFromUTC } from '../../../util/convert_timezone';
import { isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';

/**
 * TimesheetReport - Display hours by technician
 *
 * Props:
 * - locationId: number - installer location ID
 * - dateRange: { startDate, endDate }
 * - technicianId: number|null - filter by specific technician
 * - onTechniciansLoaded: function - callback with technician list for filter dropdown
 */
class TimesheetReport extends Component {
  state = {
    data: [],
    loading: true,
    error: null,
    showDaily: false
  };

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    // Reload when filters change
    if (
      prevProps.locationId !== this.props.locationId ||
      prevProps.dateRange.startDate !== this.props.dateRange.startDate ||
      prevProps.dateRange.endDate !== this.props.dateRange.endDate ||
      prevProps.technicianId !== this.props.technicianId
    ) {
      this.loadData();
    }
  }

  loadData = async () => {
    const { locationId, locations, dateRange, technicianId, onTechniciansLoaded } = this.props;
    const { showDaily } = this.state;

    if (!locationId) return;

    const isAggregate = isAllLocations(locationId) && Array.isArray(locations) && locations.length > 0;

    try {
      this.setState({ loading: true, error: null });

      let data;

      if (isAggregate) {
        const results = await Promise.all(
          locations.map(loc =>
            getTimesheetReport(loc.location_id, {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              technicianId,
              daily: showDaily
            }).catch(() => ({ data: [] }))
          )
        );

        if (showDaily) {
          // Daily mode: concatenate all results, tag with location, sort by date desc
          data = [];
          results.forEach((result, i) => {
            const rows = result.data || [];
            const loc = locations[i];
            rows.forEach(row => {
              data.push({ ...row, location_id: loc.location_id, location_name: getLocationName(loc) });
            });
          });
          data.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        } else {
          // Summary mode: group by technician_id and sum metrics
          const techMap = {};
          results.forEach((result, i) => {
            const rows = result.data || [];
            const loc = locations[i];
            rows.forEach(row => {
              const key = row.technician_id;
              if (!techMap[key]) {
                techMap[key] = {
                  technician_id: row.technician_id,
                  technician_name: row.technician_name,
                  session_count: 0,
                  total_hours: 0,
                  regular_hours: 0,
                  exception_hours: 0,
                  self_checkin_count: 0,
                  manager_checkin_count: 0,
                  _locations: new Set()
                };
              }
              techMap[key].session_count += (row.session_count || 0);
              techMap[key].total_hours += parseFloat(row.total_hours || 0);
              techMap[key].regular_hours += parseFloat(row.regular_hours || 0);
              techMap[key].exception_hours += parseFloat(row.exception_hours || 0);
              techMap[key].self_checkin_count += (row.self_checkin_count || 0);
              techMap[key].manager_checkin_count += (row.manager_checkin_count || 0);
              techMap[key]._locations.add(getLocationName(loc));
            });
          });
          data = Object.values(techMap).map(t => ({
            ...t,
            location_name: [...t._locations].join(', '),
            _locations: undefined
          }));
        }
      } else {
        const result = await getTimesheetReport(locationId, {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          technicianId,
          daily: showDaily
        });
        data = result.data || [];
      }

      this.setState({ data, loading: false });

      // Pass technician list to parent for filter dropdown (only from summary view)
      if (!showDaily && onTechniciansLoaded && data.length > 0) {
        // Deduplicate by technician_id
        const seen = new Set();
        const technicians = [];
        data.forEach(d => {
          if (!seen.has(d.technician_id)) {
            seen.add(d.technician_id);
            technicians.push({
              technician_id: d.technician_id,
              technician_name: d.technician_name
            });
          }
        });
        onTechniciansLoaded(technicians);
      }
    } catch (error) {
      console.error('Error loading timesheet report:', error);
      this.setState({
        error: 'Failed to load timesheet data',
        loading: false
      });
    }
  };

  // Toggle between summary and daily view
  toggleDailyView = () => {
    this.setState(
      prevState => ({ showDaily: !prevState.showDaily }),
      () => this.loadData()
    );
  };

  // Format hours with 2 decimal places
  formatHours = (hours) => {
    if (hours === null || hours === undefined) return '0.00';
    const num = typeof hours === 'string' ? parseFloat(hours) : hours;
    return num.toFixed(2);
  };

  // Calculate totals for summary view
  calculateTotals = () => {
    const { data } = this.state;
    return data.reduce((acc, row) => ({
      session_count: acc.session_count + (row.session_count || 0),
      total_hours: acc.total_hours + parseFloat(row.total_hours || 0),
      regular_hours: acc.regular_hours + parseFloat(row.regular_hours || 0),
      exception_hours: acc.exception_hours + parseFloat(row.exception_hours || 0),
      self_checkin_count: acc.self_checkin_count + (row.self_checkin_count || 0),
      manager_checkin_count: acc.manager_checkin_count + (row.manager_checkin_count || 0)
    }), {
      session_count: 0,
      total_hours: 0,
      regular_hours: 0,
      exception_hours: 0,
      self_checkin_count: 0,
      manager_checkin_count: 0
    });
  };

  // Check if we're in aggregate mode with multiple locations
  isShowingLocationColumn = () => {
    const { locationId, locations } = this.props;
    return isAllLocations(locationId) && Array.isArray(locations) && locations.length > 1;
  };

  // Render summary view table
  renderSummaryTable = () => {
    const { data } = this.state;
    const totals = this.calculateTotals();
    const showLocation = this.isShowingLocationColumn();

    return (
      <div className="table-responsive">
        <table className="jv-table mb-0">
          <thead>
            <tr>
              <th>Technician</th>
              {showLocation && <th>Location</th>}
              <th className="text-right">Sessions</th>
              <th className="text-right">Total Hours</th>
              <th className="text-right">Regular</th>
              <th className="text-right">Exception</th>
              <th className="text-right">Self Check-ins</th>
              <th className="text-right">Manager Check-ins</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.technician_id}>
                <td>
                  <strong>{row.technician_name}</strong>
                </td>
                {showLocation && (
                  <td>
                    <span className="jv-location-badge" style={{ backgroundColor: getLocationBadgeColor(row.location_id, this.props.locations) }}>
                      {row.location_name}
                    </span>
                  </td>
                )}
                <td className="text-right">{row.session_count || 0}</td>
                <td className="text-right">
                  <strong>{this.formatHours(row.total_hours)}</strong>
                </td>
                <td className="text-right">{this.formatHours(row.regular_hours)}</td>
                <td className="text-right">
                  {parseFloat(row.exception_hours || 0) > 0 ? (
                    <span className="text-warning">{this.formatHours(row.exception_hours)}</span>
                  ) : (
                    this.formatHours(row.exception_hours)
                  )}
                </td>
                <td className="text-right">{row.self_checkin_count || 0}</td>
                <td className="text-right">
                  {(row.manager_checkin_count || 0) > 0 ? (
                    <span className="text-info">{row.manager_checkin_count}</span>
                  ) : (
                    '0'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {data.length > 1 && (
            <tfoot className="bg-white border-top">
              <tr>
                <td><strong>Total</strong></td>
                {showLocation && <td></td>}
                <td className="text-right"><strong>{totals.session_count}</strong></td>
                <td className="text-right"><strong>{this.formatHours(totals.total_hours)}</strong></td>
                <td className="text-right"><strong>{this.formatHours(totals.regular_hours)}</strong></td>
                <td className="text-right"><strong>{this.formatHours(totals.exception_hours)}</strong></td>
                <td className="text-right"><strong>{totals.self_checkin_count}</strong></td>
                <td className="text-right"><strong>{totals.manager_checkin_count}</strong></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  // Render daily view table
  renderDailyTable = () => {
    const { data } = this.state;
    const showLocation = this.isShowingLocationColumn();

    return (
      <div className="table-responsive">
        <table className="jv-table mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Technician</th>
              {showLocation && <th>Location</th>}
              <th className="text-right">Hours</th>
              <th>First In</th>
              <th>Last Out</th>
              <th className="text-center">Exceptions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={`${row.date}-${row.technician_id}-${index}`}>
                <td>{moment(row.date).format('ddd, MMM D')}</td>
                <td>{row.technician_name}</td>
                {showLocation && (
                  <td>
                    <span className="jv-location-badge" style={{ backgroundColor: getLocationBadgeColor(row.location_id, this.props.locations) }}>
                      {row.location_name}
                    </span>
                  </td>
                )}
                <td className="text-right">
                  <strong>{this.formatHours(row.total_hours)}</strong>
                </td>
                <td>
                  {(row.first_in || row.first_session) ? convertFromUTC(row.first_in || row.first_session).format('h:mm A') : '—'}
                </td>
                <td>
                  {(row.last_out || row.last_session) ? convertFromUTC(row.last_out || row.last_session).format('h:mm A') : '—'}
                </td>
                <td className="text-center">
                  {row.has_exceptions ? (
                    <span className="jv-badge jv-badge-warning">
                      <i className="fa-sharp fa-regular fa-exclamation-triangle mr-1"></i>
                      Yes
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  render() {
    const { data, loading, error, showDaily } = this.state;

    // Loading state
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-secondary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="text-muted mt-2 mb-0">Loading timesheet data...</p>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="text-danger p-3">
          <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      );
    }

    // Empty state
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-4" style={{ background: 'transparent' }}>
          <p className="text-muted mb-0">No timesheet data for the selected period.</p>
        </div>
      );
    }

    return (
      <div>
        {/* View Toggle */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <span className="text-muted small">
              {data.length} {showDaily ? 'entries' : 'technicians'}
            </span>
          </div>
          <div className="btn-group btn-group-sm">
            <button
              className={`jv-btn jv-btn-sm ${!showDaily ? 'jv-btn-primary' : 'jv-btn-tertiary'}`}
              onClick={() => showDaily && this.toggleDailyView()}
            >
              <i className="fa-sharp fa-regular fa-users mr-1"></i>
              Summary
            </button>
            <button
              className={`jv-btn jv-btn-sm ${showDaily ? 'jv-btn-primary' : 'jv-btn-tertiary'}`}
              onClick={() => !showDaily && this.toggleDailyView()}
            >
              <i className="fa-sharp fa-regular fa-calendar mr-1"></i>
              Daily
            </button>
          </div>
        </div>

        {/* Table */}
        {showDaily ? this.renderDailyTable() : this.renderSummaryTable()}
      </div>
    );
  }
}

export default TimesheetReport;
